import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import AsyncSelect from "react-select/async";
import { components } from "react-select";
import styled from "styled-components";
import { useRouter } from "next/router";
import { useSetAtom } from "jotai";
import {
	searchContentAtom,
	type ContentSearchOption,
} from "@contexts/searchAtoms";
import { colours, addOpacityToColor } from "@helpers";

const SearchContainer = styled.div<{ $fullWidth?: boolean }>`
  width: ${({ $fullWidth }) => ($fullWidth ? "100%" : "250px")};
  margin: ${({ $fullWidth }) => ($fullWidth ? "0" : "0 15px")};

  @media (max-width: 768px) {
    width: 100%;
    margin: ${({ $fullWidth }) => ($fullWidth ? "0" : "10px 0")};
  }
`;

const SearchIcon = () => (
	<svg
		width="18"
		height="18"
		viewBox="0 0 24 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		style={{ flexShrink: 0, marginRight: "5px", color: colours.MIDNIGHT_BLUE }}
		aria-hidden="true"
	>
		<path
			d="M10.5 18a7.5 7.5 0 1 0 0-15 7.5 7.5 0 0 0 0 15Zm9-1.5 4.5 4.5"
			stroke={colours.MIDNIGHT_BLUE}
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const customStyles = {
	control: (provided: any) => ({
		...provided,
		borderRadius: "8px",
		border: "1px solid rgba(0, 0, 0, 0.4)",
		boxShadow: "0 2px 8px rgba(18, 18, 82, 0.08)",
		minHeight: "50px",
		backgroundColor: colours.WHITE,
		paddingLeft: "10px",
		paddingRight: "15px",
		paddingTop: "10px",
		paddingBottom: "10px",
		"&:hover": {
			border: "1px solid rgba(0, 0, 0, 0.4)",
			boxShadow: "0 2px 8px rgba(18, 18, 82, 0.08)",
		},
		"&:focus-within": {
			border: "1px solid rgba(0, 0, 0, 0.4)",
			boxShadow: "0 2px 8px rgba(18, 18, 82, 0.08)",
		},
	}),
	placeholder: (provided: any) => ({
		...provided,
		fontFamily: "var(--inter-regular), Inter, sans-serif",
		fontSize: "16px",
		lineHeight: 1.5,
		color: addOpacityToColor(colours.MIDNIGHT_BLUE, 0.7),
	}),
	input: (provided: any) => ({
		...provided,
		fontFamily: "var(--inter-medium), Inter, sans-serif",
		fontWeight: 500,
		fontSize: "16px",
		lineHeight: 1.5,
		color: colours.MIDNIGHT_BLUE,
		margin: 0,
		padding: 0,
		"& input": {
			fontFamily: "var(--inter-medium), Inter, sans-serif !important",
			fontWeight: "500 !important",
			fontSize: "16px !important",
			color: `${colours.MIDNIGHT_BLUE} !important`,
		},
	}),
	valueContainer: (provided: any) => ({
		...provided,
		padding: 0,
		gap: "5px",
	}),
	menu: (provided: any) => ({
		...provided,
		borderRadius: "8px",
		boxShadow: "0px 2px 15px 0px rgba(18, 18, 82, 0.2)",
		border: "none",
		overflow: "hidden",
		marginTop: "4px",
		zIndex: 9999,
	}),
	menuList: (provided: any) => ({
		...provided,
		padding: 0,
	}),
	option: (provided: any, state: any) => ({
		...provided,
		backgroundColor: state.isFocused
			? "rgba(244, 244, 247, 0.8)"
			: colours.WHITE,
		color: colours.MIDNIGHT_BLUE,
		cursor: "pointer",
		padding: 0,
		fontSize: "16px",
	}),
};

const CustomOption = (props: any) => {
	const { innerProps, innerRef, data, selectProps } = props;
	const inputValue = selectProps?.inputValue || "";
	const option = data as ContentSearchOption;
	const title = option.label;
	const snippet = option.snippet;
	const category = option.category;

	const highlightText = (text: string, highlight: string) => {
		if (!highlight || !text) return text;
		const words = highlight
			.trim()
			.split(/\s+/)
			.filter((w) => w.length >= 2);
		const termsToHighlight = words.length > 0 ? words : [highlight.trim()];
		const escaped = termsToHighlight
			.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
			.join("|");
		const parts = text.split(new RegExp(`(${escaped})`, "gi"));
		return (
			<span>
				{parts.map((part, i) => {
					const isMatch = termsToHighlight.some(
						(w) => part.toLowerCase() === w.toLowerCase(),
					);
					return isMatch ? (
						<span key={`${i}-${part}`} style={{ fontWeight: 700 }}>
							{part}
						</span>
					) : (
						<span key={`${i}-${part}`}>{part}</span>
					);
				})}
			</span>
		);
	};

	return (
		<div
			ref={innerRef}
			{...innerProps}
			style={{
				display: "flex",
				flexDirection: "column",
				alignItems: "flex-start",
				gap: "15px",
				padding: "20px 15px",
				backgroundColor: props.isFocused
					? "rgba(244, 244, 247, 0.8)"
					: colours.WHITE,
				cursor: "pointer",
				borderTop: "1px solid rgba(18, 18, 82, 0.2)",
				color: colours.MIDNIGHT_BLUE,
			}}
		>
			{category && (
				<div
					style={{
						maxWidth: "100%",
						backgroundColor: "rgba(244, 244, 247, 0.8)",
						border: "0.5px solid rgba(18, 18, 78, 0.7)",
						borderRadius: "10px",
						padding: "5px 12px",
						fontFamily: "var(--euclid-regular), Euclid Circular A, sans-serif",
						fontSize: "14px",
						lineHeight: 1.5,
						color: colours.MIDNIGHT_BLUE,
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
					}}
				>
					{category}
				</div>
			)}
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					gap: "10px",
					width: "100%",
					minWidth: 0,
				}}
			>
				<p
					style={{
						margin: 0,
						fontFamily: "var(--euclid-medium), Euclid Circular A, sans-serif",
						fontSize: "18px",
						lineHeight: 1.3,
						color: colours.MIDNIGHT_BLUE,
						textDecoration: "underline",
					}}
				>
					{highlightText(title, inputValue)}
				</p>
				{snippet && (
					<p
						style={{
							margin: 0,
							fontFamily:
								"var(--euclid-regular), Euclid Circular A, sans-serif",
							fontSize: "15px",
							lineHeight: 1.4,
							color: colours.MIDNIGHT_BLUE,
						}}
					>
						{highlightText(snippet, inputValue)}
					</p>
				)}
			</div>
		</div>
	);
};

const SeeAllOption = (props: any) => {
	const { innerProps, innerRef, data } = props;
	return (
		<div
			ref={innerRef}
			{...innerProps}
			style={{
				padding: "18px 30px",
				backgroundColor: props.isFocused
					? "rgba(244, 244, 247, 0.9)"
					: "#f4f4f7",
				cursor: "pointer",
				borderTop: "0.5px solid rgba(18, 18, 82, 0.2)",
				textAlign: "center",
				fontWeight: 700,
				fontSize: "16px",
				lineHeight: 1.5,
				color: colours.MIDNIGHT_BLUE,
				fontFamily: "var(--euclid-regular), Euclid Circular A, sans-serif",
			}}
		>
			{data.label}
		</div>
	);
};

const NullComponent = () => null;

const CustomControl = (props: any) => (
	<components.Control {...props}>
		<div
			style={{
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				padding: "5px",
				flexShrink: 0,
				color: addOpacityToColor(colours.MIDNIGHT_BLUE, 0.7),
			}}
		>
			<SearchIcon />
		</div>
		{props.children}
	</components.Control>
);

const CustomMenu = (props: any) => (
	<div data-search-menu style={{ display: "contents" }}>
		<components.Menu {...props} />
	</div>
);

const CustomOptionRouter = (props: any) => {
	if (props.data.value?.startsWith("__search_all__")) {
		return <SeeAllOption {...props} />;
	}
	return <CustomOption {...props} />;
};

const searchBarComponents = {
	Control: CustomControl,
	DropdownIndicator: NullComponent,
	IndicatorSeparator: NullComponent,
	Menu: CustomMenu,
	Option: CustomOptionRouter,
};

interface SearchBarProps {
	fullWidth?: boolean;
}

const SearchBar = ({ fullWidth }: SearchBarProps) => {
	const router = useRouter();
	const searchContent = useSetAtom(searchContentAtom);
	const [menuIsOpen, setMenuIsOpen] = useState(false);
	const initialQuery = typeof router.query.q === "string" ? router.query.q : "";
	const inputValueRef = useRef(initialQuery);
	const [selectKey, setSelectKey] = useState(initialQuery);

	useEffect(() => {
		if (typeof router.query.q === "string") {
			inputValueRef.current = router.query.q;
			setSelectKey(router.query.q);
		}
	}, [router.query.q]);

	const submitSearch = (rawQuery: string) => {
		const query = rawQuery.trim();
		if (!query) return;
		setMenuIsOpen(false);
		router.push(`/help/search?q=${encodeURIComponent(query)}`);
	};

	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const isInsideSearchOrMenu = (target: EventTarget | null): boolean => {
			if (!target || !(target instanceof HTMLElement)) return false;
			if (target.closest?.("[data-search-menu]")) return true;
			if (containerRef.current?.contains(target)) return true;
			return false;
		};
		const handleScroll = (e: Event) => {
			if (!menuIsOpen) return;
			if (isInsideSearchOrMenu(e.target)) return;
			setMenuIsOpen(false);
		};
		const handleWheel = (e: Event) => {
			if (!menuIsOpen) return;
			if (isInsideSearchOrMenu(e.target)) return;
			setMenuIsOpen(false);
		};
		const handleTouchMove = (e: Event) => {
			if (!menuIsOpen) return;
			if (isInsideSearchOrMenu(e.target)) return;
			setMenuIsOpen(false);
		};
		window.addEventListener("scroll", handleScroll, {
			capture: true,
			passive: true,
		});
		window.addEventListener("wheel", handleWheel, {
			capture: true,
			passive: true,
		});
		window.addEventListener("touchmove", handleTouchMove, {
			capture: true,
			passive: true,
		});
		return () => {
			window.removeEventListener("scroll", handleScroll, { capture: true });
			window.removeEventListener("wheel", handleWheel, { capture: true });
			window.removeEventListener("touchmove", handleTouchMove, {
				capture: true,
			});
		};
	}, [menuIsOpen]);

	const loadOptions = async (
		inputValue: string,
	): Promise<ContentSearchOption[]> => {
		if (!inputValue || inputValue.length < 2) return [];

		try {
			const results = await searchContent(inputValue);
			if (results.length > 0) {
				return [
					...results,
					{
						label: `All results for "${inputValue}"`,
						value: `__search_all__${inputValue}`,
						urls: [`/help/search?q=${encodeURIComponent(inputValue)}`],
					},
				];
			}
			return results;
		} catch (error) {
			console.error("Error searching contentful:", error);
			return [];
		}
	};

	const handleChange = (selectedOption: ContentSearchOption | null) => {
		if (!selectedOption) return;

		if (selectedOption.value.startsWith("__search_all__")) {
			const query = selectedOption.value.replace("__search_all__", "");
			router.push(`/help/search?q=${encodeURIComponent(query)}`);
			return;
		}

		if (selectedOption.urls?.length) {
			const targetUrl = selectedOption.urls[0].trim();
			router.push(targetUrl);
		}
	};

	return (
		<SearchContainer $fullWidth={fullWidth} ref={containerRef}>
			<AsyncSelect
				cacheOptions
				loadOptions={loadOptions}
				onChange={handleChange as any}
				key={selectKey}
				defaultInputValue={selectKey}
				onInputChange={(val, action) => {
					if (action.action === "input-change") {
						inputValueRef.current = val;
						if (val.length >= 2) {
							setMenuIsOpen(true);
						}
					}
				}}
				onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
					if (e.key === "Enter" && inputValueRef.current.trim()) {
						e.preventDefault();
						e.stopPropagation();
						submitSearch(inputValueRef.current);
					}
				}}
				placeholder="Search felix Help"
				styles={customStyles}
				menuIsOpen={menuIsOpen}
				onBlur={() => setMenuIsOpen(false)}
				onMenuOpen={() => setMenuIsOpen(true)}
				onMenuClose={() => setMenuIsOpen(false)}
				menuPortalTarget={
					typeof document !== "undefined" ? document.body : undefined
				}
				menuPosition="fixed"
				noOptionsMessage={({ inputValue }) =>
					!inputValue ? "Type to search" : "No results found"
				}
				components={searchBarComponents}
			/>
		</SearchContainer>
	);
};

export default SearchBar;
